import { useToast } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { useGetMeetingQuery } from '@/@generated/codegen/API';
import {
  LoggedType,
  useUserAuthContext,
} from '@/components/AuthTokenContextProvider/UserAuthContext';
import LiveMeetingView from '@/pages/caseCluster/LiveMeetingView';
import MeetingFinishedView from '@/pages/meeting/MeetingFinishedView';
import MeetingNotStartedView from '@/pages/meeting/MeetingNotStartedView';
import { ROUTES } from '@/routes/Routes';
import { PageLoading } from '@/ui/PageLoading';

import { useNotInMeetingContext } from './context/CurrentMeetingDataContext';
import { MEETING_STATUS } from './helpers/getMeetingStatus';
import useMeetingStatusSubscription from './hooks/useMeetingStatusSubscription';
import MeetingStatsCreationContextProvider from '../meeting-stats/context/MeetingStatsCreationContext';

/*
NOTE: This component is a wrapper for LiveMeetingView that:
- creates a guest account if he has a correct guestUrlCode and doesn't already have an account
- checks if the user is allowed to enter (has accedd to the meeting)
- checks if the meeting has start or ended, edge cases like that
*/
export default function LiveMeetingPermissions() {
  const { meetingId: urlMeetingId } = useParams();
  const navigate = useNavigate();
  const { status: isInMeetingStatus, setCurrentMeetingId } = useNotInMeetingContext();
  const { status: userStatus } = useUserAuthContext();
  //this is the meeting the user wants to join, but hasn't yet
  const futureMeetingQuery = useGetMeetingQuery({
    variables: {
      where: {
        id: urlMeetingId,
      },
    },
    skip: !urlMeetingId,
  });
  const futureMeeting = useMemo(() => {
    return futureMeetingQuery.data?.getMeeting;
  }, [futureMeetingQuery.data]);

  const toast = useToast();
  const meetingStatus = useMeetingStatusSubscription(urlMeetingId);
  const isLoading = futureMeetingQuery.loading;

  useEffect(() => {
    if (!urlMeetingId) return;
    void futureMeetingQuery.refetch();
  }, [meetingStatus]);

  useEffect(() => {
    if (!urlMeetingId) return;
    if (userStatus !== LoggedType.LOGGED_IN) return;
    // NOTE: if it's an existing meeting, and user has access to it (since he could request it)
    if (futureMeetingQuery.data) {
      setCurrentMeetingId(urlMeetingId);
    }
  }, [userStatus, urlMeetingId, futureMeetingQuery.data]);

  useEffect(() => {
    if (futureMeetingQuery.error) {
      toast({
        title: 'Error loading meeting',
        description: futureMeetingQuery.error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      navigate(ROUTES.HOME);
    }
  }, [futureMeetingQuery.error, toast, navigate]);

  if (!urlMeetingId) return <Navigate to={ROUTES.HOME} replace={true} state={{}} />;

  if (isLoading) {
    return <PageLoading />;
  }

  /*
  meeting is not yet ready
  */
  if (futureMeeting && meetingStatus === MEETING_STATUS.IDLE) {
    return <MeetingNotStartedView openDate={new Date(futureMeeting.eventDate)} />;
  }

  /*
  meeting ready, but not yet started, display a waiting screen.
  */
  if (futureMeeting && meetingStatus === MEETING_STATUS.READY) {
    return <MeetingNotStartedView openDate={new Date(futureMeeting.eventDate)} />;
  }

  /*
   meeting is done
  */
  if (
    (futureMeeting && meetingStatus === MEETING_STATUS.ENDED) ||
    meetingStatus === MEETING_STATUS.ARCHIVED
  ) {
    return <MeetingFinishedView />;
  }

  return (
    <>
      {isInMeetingStatus === 'in-meeting' ? (
        <MeetingStatsCreationContextProvider>
          <LiveMeetingView />
        </MeetingStatsCreationContextProvider>
      ) : (
        <PageLoading />
      )}
    </>
  );
}
